var _excluded = ["children", "value", "as", "placeholder", "isInvalid", "isLoading", "isReadOnly", "isDisabled", "isViewMode", "acceptTypes", "onChange", "onOpenImagePreview", "setFileDataURL", "setSelectedFile", "selectedFile", "hiddenFileInputRef", "errorMessage", "size", "replaceFileLabel", "deleteFileLabel", "aria-label", "aria-required", "aria-describedby", "id", "name"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { isNull } from 'lodash';
import React, { useEffect } from 'react';
import { Container, Group } from '../../../containers';
import { Icon } from '../../../foundations/Icon';
import { Loader } from '../../../foundations/Loader';
import { forwardRef, useUpdateEffect } from '../../../utils';
import { getInputFileAcceptByType } from '../../../utils/get-input-file-accept';
import { useMultiStyleConfig } from '../../../utils/use-style-config';
import { NakedButton } from '../../action';
import { Text } from '../../dataDisplay';
import { Box } from '../Box';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

/**
 * @private For internal use only.
 */
export var _BaseFileUpload = forwardRef((_ref, ref) => {
  var {
    children,
    value,
    as = 'div',
    placeholder,
    isInvalid,
    isLoading,
    isReadOnly,
    isDisabled,
    isViewMode,
    acceptTypes,
    onChange,
    onOpenImagePreview,
    setFileDataURL,
    setSelectedFile,
    selectedFile,
    hiddenFileInputRef,
    errorMessage,
    size,
    replaceFileLabel,
    deleteFileLabel,
    'aria-label': ariaLabel = 'Upload file',
    'aria-required': ariaRequired,
    'aria-describedby': ariaDescribedBy,
    id,
    name
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var styles = useMultiStyleConfig('_BaseFileUpload', {
    size,
    value
  });
  var shouldShowButtons = selectedFile && !isDisabled && !isReadOnly;
  var isEnabled = !isReadOnly && !isViewMode && !isDisabled;
  /* Is used to pass the aria-label to the correct element to avoid accessability violations  */

  var fieldAttributes = {
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-required': ariaRequired,
    name
  };
  useUpdateEffect(() => {
    // Since we want to trigger `null` value, we check only if the value is defined.
    value !== undefined && setSelectedFile(value);
  }, [value]);

  var handleClick = () => {
    var _hiddenFileInputRef$c;

    if (isLoading) {
      return;
    }

    onOpenImagePreview === null || onOpenImagePreview === void 0 ? void 0 : onOpenImagePreview();
    (_hiddenFileInputRef$c = hiddenFileInputRef.current) === null || _hiddenFileInputRef$c === void 0 ? void 0 : _hiddenFileInputRef$c.click();
  };

  var handleChange = event => {
    event.stopPropagation();
    var {
      target: {
        files
      }
    } = event;

    if (files !== null && files !== void 0 && files[0]) {
      setSelectedFile(files[0]);
      onChange === null || onChange === void 0 ? void 0 : onChange(files[0]);
    }
  };

  var handleDelete = event => {
    event.stopPropagation();

    if (hiddenFileInputRef.current) {
      hiddenFileInputRef.current.value = '';
    }

    setSelectedFile(null);
    onChange === null || onChange === void 0 ? void 0 : onChange(null);
  };

  useEffect(() => {
    var fileReader;
    var isCancel = false;
    var isFileObject = !!selectedFile && selectedFile instanceof File;

    if (isFileObject) {
      fileReader = new FileReader();

      fileReader.onload = e => {
        if (!e.target) return;
        var {
          result
        } = e.target;

        if (result && !isCancel && !isNull(result)) {
          setFileDataURL(result);
        }
      };

      fileReader.readAsDataURL(selectedFile);
    } else {
      setFileDataURL(null);
    }

    return () => {
      isCancel = true;

      if (fileReader && fileReader.readyState === 1) {
        fileReader.abort();
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);
  useEffect(() => {
    if (isInvalid) {
      setSelectedFile(null);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isInvalid]);

  var placeholderComponent = /*#__PURE__*/_jsxs(Box, {
    __css: styles['placeholder'],
    children: [/*#__PURE__*/_jsx(Icon, {
      type: "file-add",
      color: "inherit",
      "aria-hidden": true
    }), /*#__PURE__*/_jsx(Box, {
      __css: styles['placeholderLabel'],
      "aria-disabled": isDisabled,
      "data-readonly": isReadOnly || null,
      children: placeholder
    })]
  });

  var buttonsOnSelectComponent = /*#__PURE__*/_jsxs(Box, {
    __css: styles['buttonsContainer'],
    children: [/*#__PURE__*/_jsx(NakedButton, {
      variant: "primary",
      onClick: handleClick,
      "data-testid": "replace-file",
      isDisabled: isDisabled || isReadOnly,
      label: replaceFileLabel !== null && replaceFileLabel !== void 0 ? replaceFileLabel : 'Replace'
    }), /*#__PURE__*/_jsx(NakedButton, {
      variant: "critical",
      onClick: handleDelete,
      "data-testid": "delete-file",
      isDisabled: isDisabled || isReadOnly,
      label: deleteFileLabel !== null && deleteFileLabel !== void 0 ? deleteFileLabel : 'Delete'
    })]
  });

  var inputComponent = /*#__PURE__*/_jsx(Box, _objectSpread({
    as: "input" // ts-ignore is used due to `type` issues with `<Box/>`.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    ,
    type: "file",
    "data-testid": "upload-file-input",
    accept: getInputFileAcceptByType(acceptTypes),
    ref: hiddenFileInputRef,
    style: {
      display: 'none'
    },
    onChange: handleChange,
    placeholder: placeholder,
    disabled: isDisabled // Is used to link between the label to the field, when clicking the label it focuses the field
    ,
    id: id
  }, fieldAttributes));

  var showLargeFileUploadFooter = size === 'large' && (selectedFile && buttonsOnSelectComponent || errorMessage);
  return /*#__PURE__*/_jsxs(Box, {
    __css: styles['outerContainer'],
    children: [/*#__PURE__*/_jsxs(Box, _objectSpread(_objectSpread({
      ref: ref,
      onClick: !isReadOnly && !selectedFile ? handleClick : undefined,
      "aria-invalid": isInvalid,
      "data-loading": isLoading,
      "data-readonly": isReadOnly || isViewMode || null,
      "aria-disabled": isDisabled,
      "data-selected": !!selectedFile || null,
      __css: styles['innerContainer'],
      tabIndex: isEnabled && !selectedFile && !isLoading ? 0 : -1,
      as: as
    }, props), {}, {
      children: [isLoading && /*#__PURE__*/_jsx(Container, {
        paddingY: "xxs",
        children: /*#__PURE__*/_jsx(Loader, {})
      }), !isLoading && !selectedFile && placeholderComponent, !isLoading && selectedFile && children, shouldShowButtons && size === 'small' && buttonsOnSelectComponent, inputComponent]
    })), showLargeFileUploadFooter && /*#__PURE__*/_jsxs(Group, {
      spacing: "xs",
      variant: "vertical",
      alignItems: "center",
      children: [selectedFile && buttonsOnSelectComponent, errorMessage && /*#__PURE__*/_jsx(Text, {
        textStyle: "body4",
        color: "critical.main",
        children: errorMessage
      })]
    })]
  });
});
_BaseFileUpload.displayName = '_BaseFileUpload';
//# sourceMappingURL=_BaseFileUpload.js.map